import React, { useState, useEffect } from "react";
import axios from "axios";
import { startRegistration } from "@simplewebauthn/browser";
import Button from "../common/Button";
import Input from "../common/Input";
import BackupCodesDisplay from "./BackupCodesDisplay";

interface MfaMethod {
  id: string;
  method: string;
  isPrimary: boolean;
  verified: boolean;
}

interface WebAuthnCredential {
  id: string;
  friendly_name: string;
  device_type: string;
  created_at: string;
  last_used_at: string | null;
}

interface TrustedDevice {
  id: string;
  user_agent: string;
  ip_address: string;
  friendly_name: string;
  expires_at: string;
  last_used_at: string | null;
  created_at: string;
}

const MFASettings: React.FC = () => {
  const [methods, setMethods] = useState<MfaMethod[]>([]);
  const [webauthnCreds, setWebauthnCreds] = useState<WebAuthnCredential[]>([]);
  const [trustedDevices, setTrustedDevices] = useState<TrustedDevice[]>([]);
  const [backupCodesCount, setBackupCodesCount] = useState(0);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [loading, setLoading] = useState(true);

  // Setup state
  const [showTotpSetup, setShowTotpSetup] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [manualEntryKey, setManualEntryKey] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [webauthnName, setWebauthnName] = useState("");
  const [showWebAuthnModal, setShowWebAuthnModal] = useState(false);
  const [registeringWebAuthn, setRegisteringWebAuthn] = useState(false);
  const [showEmailSetup, setShowEmailSetup] = useState(false);
  const [emailChallengeId, setEmailChallengeId] = useState<string | null>(null);
  const [emailCode, setEmailCode] = useState("");
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get("/api/mfa/status");
      setMethods(data.methods || []);
      setWebauthnCreds(data.webauthnCredentials || []);
      setBackupCodesCount(data.backupCodesRemaining || 0);
      setMfaRequired(data.mfaRequired || false);
    } catch (err) {
      console.error("Error fetching MFA status:", err);
    } finally {
      setLoading(false);
    }

    // Separate call, separate failure. Folded into the try above, a trusted
    // devices error would blank the method list and show "Not enabled" for
    // methods the user actually has.
    try {
      const { data: devices } = await axios.get("/api/mfa/trusted-devices");
      setTrustedDevices(devices || []);
    } catch (err) {
      console.error("Error fetching trusted devices:", err);
    }
  };

  const handleAddTotp = async () => {
    try {
      setError("");
      const { data } = await axios.post("/api/mfa/totp/setup");
      setQrCodeDataUrl(data.qrCodeDataUrl);
      setManualEntryKey(data.manualEntryKey);
      setShowTotpSetup(true);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to start TOTP setup");
    }
  };

  const handleVerifyTotp = async () => {
    try {
      setError("");
      const { data } = await axios.post("/api/mfa/totp/verify-setup", {
        code: totpCode,
      });
      if (data.backupCodes) {
        setBackupCodes(data.backupCodes);
        setShowBackupCodes(true);
      }
      setShowTotpSetup(false);
      setTotpCode("");
      fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || "Invalid code");
    }
  };

  const handleRemoveTotp = async () => {
    try {
      await axios.delete("/api/mfa/totp");
      fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to remove TOTP");
    }
  };

  // Send-then-verify: the emailed code is what proves the mailbox is reachable,
  // so enabling only happens in handleVerifyEmail.
  const handleAddEmail = async () => {
    try {
      setError("");
      const { data } = await axios.post("/api/mfa/email/setup");
      setEmailChallengeId(data.challengeId);
      setEmailCode("");
      setShowEmailSetup(true);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to send verification code");
    }
  };

  const handleVerifyEmail = async () => {
    try {
      setError("");
      const { data } = await axios.post("/api/mfa/email/verify-setup", {
        challengeId: emailChallengeId,
        code: emailCode,
      });
      setShowEmailSetup(false);
      setEmailChallengeId(null);
      setEmailCode("");
      if (data.backupCodes) {
        setBackupCodes(data.backupCodes);
        setShowBackupCodes(true);
      }
      fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || "Invalid code. Please try again.");
      setEmailCode("");
    }
  };

  const handleRemoveEmail = async () => {
    try {
      await axios.delete("/api/mfa/email");
      fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to remove email OTP");
    }
  };

  // The name is only a label for the credential list (so "MacBook Touch
  // ID" and "YubiKey" can be told apart); the browser prompt that follows
  // is what actually registers the device.
  const handleAddWebAuthn = async () => {
    try {
      setError("");
      setRegisteringWebAuthn(true);
      const { data: options } = await axios.post(
        "/api/mfa/webauthn/register-options"
      );
      const response = await startRegistration({ optionsJSON: options });
      const { data } = await axios.post("/api/mfa/webauthn/register-verify", {
        response,
        friendlyName: webauthnName.trim() || "Security Key",
      });
      if (data.backupCodes) {
        setBackupCodes(data.backupCodes);
        setShowBackupCodes(true);
      }
      setWebauthnName("");
      setShowWebAuthnModal(false);
      fetchStatus();
    } catch (err: any) {
      // NotAllowedError is the user dismissing the browser prompt; keep the
      // modal open so they can try again or cancel.
      if (err.name !== "NotAllowedError") {
        setShowWebAuthnModal(false);
        setError(
          err.response?.data?.error || "Failed to register device"
        );
      }
    } finally {
      setRegisteringWebAuthn(false);
    }
  };

  const handleRemoveWebAuthn = async (credId: string) => {
    try {
      await axios.delete(`/api/mfa/webauthn/${encodeURIComponent(credId)}`);
      fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to remove credential");
    }
  };

  const handleRegenerateBackupCodes = async () => {
    try {
      const { data } = await axios.post("/api/mfa/backup-codes/regenerate");
      setBackupCodes(data.backupCodes);
      setShowBackupCodes(true);
      fetchStatus();
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to regenerate backup codes"
      );
    }
  };

  const handleRevokeTrustedDevice = async (deviceId: string) => {
    try {
      await axios.delete(`/api/mfa/trusted-devices/${deviceId}`);
      fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to revoke device");
    }
  };

  const handleRevokeAllDevices = async () => {
    try {
      await axios.delete("/api/mfa/trusted-devices");
      fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to revoke devices");
    }
  };

  if (loading) return <div className="p-4 text-base-content/60">Loading...</div>;

  const hasTotp = methods.some((m) => m.method === "totp" && m.verified);
  const hasEmail = methods.some((m) => m.method === "email");
  const hasWebAuthn = methods.some((m) => m.method === "webauthn");

  // Same row anatomy as the Account rows above this section (Settings.tsx
  // `Setting`): bold label + muted line on the left, one outline (or danger)
  // button on the right, `pb-6` between rows. Setup steps expand under
  // their row in a bordered panel.
  // Same horizontal padding as the Preferences controls (TextInput /
  // Dropdown size="sm") and the Account rows above, so every settings row
  // button lines up.
  const ROW_BTN = "px-1 md:px-4";
  const Row: React.FC<{ label: string; value: React.ReactNode; action?: React.ReactNode; className?: string }> = ({ label, value, action, className }) => (
    <div className={`flex items-center justify-between gap-4 ${className ?? "pb-6"}`}>
      <div className="flex flex-col min-w-0">
        <p className="m-0 font-bold text-sm">{label}</p>
        <p className="m-0 text-base-content/60 text-sm">{value}</p>
      </div>
      {action && <div className="flex items-center gap-2 shrink-0">{action}</div>}
    </div>
  );
  const Panel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="border border-base-300 rounded-lg p-4 mb-6 space-y-3">{children}</div>
  );
  const SubRow: React.FC<{ name: string; meta: string; onRemove: () => void; removeLabel: string }> = ({ name, meta, onRemove, removeLabel }) => (
    <div className="flex items-center justify-between py-2 px-3 bg-base-200 rounded-lg">
      <div className="min-w-0 truncate">
        <span className="text-sm font-medium">{name}</span>
        <span className="text-xs text-base-content/40 ml-2">{meta}</span>
      </div>
      <button type="button" onClick={onRemove} className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 shrink-0">
        {removeLabel}
      </button>
    </div>
  );

  return (
    <div>
      {mfaRequired && (
        <p className="m-0 pb-6 text-sm text-base-content/60">Multi-factor authentication is required by your agency.</p>
      )}

      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 text-sm rounded-lg p-3">
          {error}
        </div>
      )}

      {showBackupCodes && backupCodes.length > 0 && (
        <Panel>
          <BackupCodesDisplay
            codes={backupCodes}
            onContinue={() => {
              setShowBackupCodes(false);
              setBackupCodes([]);
            }}
          />
        </Panel>
      )}

      {/* Authenticator App */}
      <Row
        label="Authenticator App"
        value={hasTotp ? "Configured" : "Not configured"}
        className={showTotpSetup ? "pb-3" : "pb-6"}
        action={
          hasTotp ? (
            <Button variant="danger" size="sm" className={ROW_BTN} onClick={handleRemoveTotp}>
              Remove
            </Button>
          ) : (
            <Button variant="outline" size="sm" className={ROW_BTN} onClick={handleAddTotp}>
              Set Up
            </Button>
          )
        }
      />
      {showTotpSetup && (
        <Panel>
          {qrCodeDataUrl && (
            <div className="text-center">
              <img src={qrCodeDataUrl} alt="TOTP QR Code" className="inline-block" width={160} height={160} />
              <p className="text-xs text-base-content/60 mt-2 font-mono break-all">{manualEntryKey}</p>
            </div>
          )}
          <div className="flex gap-2">
            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={totpCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTotpCode(e.target.value.replace(/\D/g, ""))}
              placeholder="6-digit code"
              className="text-center"
            />
            <Button variant="primary" size="sm" onClick={handleVerifyTotp} disabled={totpCode.length !== 6}>
              Verify
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setShowTotpSetup(false); setTotpCode(""); }}>
              Cancel
            </Button>
          </div>
        </Panel>
      )}

      {/* Email OTP */}
      <Row
        label="Email Verification"
        value={hasEmail ? "Enabled" : "Not enabled"}
        className={showEmailSetup && !hasEmail ? "pb-3" : "pb-6"}
        action={
          hasEmail ? (
            <Button variant="danger" size="sm" className={ROW_BTN} onClick={handleRemoveEmail}>
              Remove
            </Button>
          ) : (
            <Button variant="outline" size="sm" className={ROW_BTN} onClick={handleAddEmail}>
              Enable
            </Button>
          )
        }
      />
      {showEmailSetup && !hasEmail && (
        <Panel>
          <p className="m-0 text-sm text-base-content/70">Enter the 6-digit code we just emailed you.</p>
          <Input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={emailCode}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailCode(e.target.value.replace(/\D/g, ""))}
            placeholder="000000"
            className="text-center text-xl tracking-widest w-full !max-w-none"
            autoFocus
          />
          <div className="flex gap-2">
            <Button variant="primary" size="sm" onClick={handleVerifyEmail} disabled={emailCode.length !== 6}>
              Verify &amp; Enable
            </Button>
            <Button variant="outline" size="sm" onClick={handleAddEmail}>
              Resend
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowEmailSetup(false);
                setEmailChallengeId(null);
                setEmailCode("");
              }}
            >
              Cancel
            </Button>
          </div>
        </Panel>
      )}

      {/* WebAuthn / Device Biometrics */}
      <Row
        label="Device Biometrics"
        value={
          webauthnCreds.length
            ? `${webauthnCreds.length} device${webauthnCreds.length === 1 ? "" : "s"} registered`
            : "Touch ID, Face ID, Windows Hello, or security keys"
        }
        className={webauthnCreds.length ? "pb-3" : "pb-6"}
        action={
          <Button variant="outline" size="sm" className={ROW_BTN} onClick={() => { setWebauthnName(""); setShowWebAuthnModal(true); }}>
            Add
          </Button>
        }
      />
      {/* Same modal anatomy as the Account rows' "Update …" dialogs. */}
      <dialog className={`modal ${showWebAuthnModal ? "modal-open" : ""}`}>
        <div className="modal-box max-w-96">
          <h3 className="font-bold text-lg mt-2">Add a device</h3>
          <p className="text-sm text-base-content/60 m-0 pb-4">
            Give this device a name so you can tell it apart later, then follow your browser's prompt to register Touch ID, Face ID, Windows Hello, or a security key.
          </p>
          <div className="py-1">
            <Input
              type="text"
              value={webauthnName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWebauthnName(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === "Enter" && !registeringWebAuthn) handleAddWebAuthn(); }}
              placeholder="e.g. MacBook Touch ID"
              className="!w-full !max-w-full"
              autoFocus={showWebAuthnModal}
              disabled={registeringWebAuthn}
            />
          </div>
          <div className="modal-action">
            <Button variant="outline" onClick={() => setShowWebAuthnModal(false)} disabled={registeringWebAuthn}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddWebAuthn} disabled={registeringWebAuthn}>
              {registeringWebAuthn ? "Waiting for device…" : "Register"}
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button type="button" onClick={() => !registeringWebAuthn && setShowWebAuthnModal(false)}>close</button>
        </form>
      </dialog>
      {webauthnCreds.length > 0 && (
        <div className="space-y-2 pb-6">
          {webauthnCreds.map((cred) => (
            <SubRow
              key={cred.id}
              name={cred.friendly_name}
              meta={`Added ${new Date(cred.created_at).toLocaleDateString()}`}
              onRemove={() => handleRemoveWebAuthn(cred.id)}
              removeLabel="Remove"
            />
          ))}
        </div>
      )}

      {/* Backup Codes */}
      {(hasTotp || hasEmail || hasWebAuthn) && (
        <Row
          label="Backup Codes"
          value={`${backupCodesCount} unused code${backupCodesCount === 1 ? "" : "s"} remaining`}
          action={
            <Button variant="outline" size="sm" className={ROW_BTN} onClick={handleRegenerateBackupCodes}>
              Regenerate
            </Button>
          }
        />
      )}

      {/* Trusted Devices */}
      {trustedDevices.length > 0 && (
        <>
          <Row
            label="Trusted Devices"
            value="Devices that can skip MFA verification"
            className="pb-3"
            action={
              <Button variant="danger" size="sm" className={ROW_BTN} onClick={handleRevokeAllDevices}>
                Revoke All
              </Button>
            }
          />
          <div className="space-y-2 pb-6">
            {trustedDevices.map((device) => (
              <SubRow
                key={device.id}
                name={device.friendly_name}
                meta={`Last used ${device.last_used_at ? new Date(device.last_used_at).toLocaleDateString() : "never"}`}
                onRemove={() => handleRevokeTrustedDevice(device.id)}
                removeLabel="Revoke"
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MFASettings;
